import { useState } from "react";
import { SectionTitle } from "../_shared/section-title";
import { PageShell, Stack } from "../_shared/page-shell";
import { Card } from "../_shared/card";
import { IconCamera, IconChevronRight, IconClock } from "../_shared/icons";
import { navigate } from "../_shared/router";
import {
  children,
  photoMonths,
  photoSessions,
  type Child,
} from "../_data/mock";

const childPill = {
  orange: "bg-[var(--child-1-light)] text-[var(--child-1-primary)]",
  blue: "bg-[var(--child-2-light)] text-[var(--child-2-primary)]",
} as const;

const childRing = {
  orange:
    "ring-[var(--child-1-primary)] bg-[var(--child-1-light)] text-[var(--child-1-primary)]",
  blue: "ring-[var(--child-2-primary)] bg-[var(--child-2-light)] text-[var(--child-2-primary)]",
} as const;

export function PhotosPage() {
  const [childId, setChildId] = useState<Child["id"]>(
    children[0]?.id ?? "jeanne",
  );
  const pendingSessions = photoSessions.filter(
    (s) => s.status !== "done" && s.childId === childId,
  );
  return (
    <>
      <SectionTitle title="Photos" />
      <PageShell>
        <Stack>
          <Card title="Pour qui ?">
            <ul className="flex flex-wrap gap-2 pt-1">
              {children.map((c) => {
                const selected = childId === c.id;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => setChildId(c.id)}
                      className={`inline-flex h-10 items-center rounded-full px-4 text-[14px] font-semibold ring-1 transition ${selected ? childRing[c.color] : "bg-[var(--surface-1)] text-[var(--text-2)] ring-[var(--border-1)] hover:bg-[var(--surface-2)]"}`}
                    >
                      {c.firstName}
                    </button>
                  </li>
                );
              })}
            </ul>
          </Card>

          <Card
            title="Séances photos"
            icon={<IconCamera width={20} height={20} />}
            badge={pendingSessions.length}
            onClick={() => navigate("photo-sessions")}
          >
            <p className="text-[13px] text-[var(--text-2)]">
              {pendingSessions.length > 0
                ? `${pendingSessions.length} séance${pendingSessions.length > 1 ? "s" : ""} en cours — protocoles à réaliser pour le cabinet.`
                : "Aucune séance en attente."}
            </p>
            <ul className="mt-3 space-y-2">
              {pendingSessions.slice(0, 2).map((s) => {
                const child = children.find((c) => c.id === s.childId);
                if (!child) return null;
                return (
                  <li
                    key={s.id}
                    className="flex items-center justify-between gap-3 rounded-2xl border border-[var(--border-1)] bg-[var(--surface-1)] p-3"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-[13.5px] font-semibold text-[var(--text-1)]">
                        {s.label}
                      </p>
                      <p className="mt-0.5 flex items-center gap-2 text-[12px] text-[var(--text-2)]">
                        <span
                          className={`inline-flex h-5 items-center rounded-full px-2 text-[11px] font-semibold ${childPill[child.color]}`}
                        >
                          {child.firstName}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <IconClock
                            width={12}
                            height={12}
                            className="text-[var(--text-3)]"
                          />
                          {s.dueDate}
                        </span>
                      </p>
                    </div>
                    <IconChevronRight
                      width={16}
                      height={16}
                      className="shrink-0 text-[var(--text-3)]"
                    />
                  </li>
                );
              })}
            </ul>
          </Card>

          <div className="flex flex-wrap gap-2 px-1">
            <button
              type="button"
              className="inline-flex h-9 items-center rounded-full bg-[var(--surface-1)] px-3.5 text-[12.5px] font-semibold text-[var(--text-2)] ring-1 ring-[var(--border-1)] transition hover:bg-[var(--surface-2)]"
            >
              Toutes
            </button>
            <button
              type="button"
              className="inline-flex h-9 items-center rounded-full bg-[var(--surface-1)] px-3.5 text-[12.5px] font-semibold text-[var(--text-2)] ring-1 ring-[var(--border-1)] transition hover:bg-[var(--surface-2)]"
            >
              Premières
            </button>
            <button
              type="button"
              className="inline-flex h-9 items-center rounded-full bg-[var(--surface-1)] px-3.5 text-[12.5px] font-semibold text-[var(--text-2)] ring-1 ring-[var(--border-1)] transition hover:bg-[var(--surface-2)]"
            >
              Dernières
            </button>
          </div>

          {photoMonths.map((m) => (
            <Card key={m.id} title={m.month}>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
                {Array.from({ length: m.count }).map((_, i) => (
                  <div
                    key={i}
                    className="aspect-square rounded-lg bg-linear-to-br from-gray-200 to-gray-300"
                  />
                ))}
              </div>
            </Card>
          ))}
        </Stack>
      </PageShell>
    </>
  );
}
